import React, { useState } from 'react';
import { useFetch } from '../../hooks/useFetch';
import { Spinner, ErrorBanner } from '../../components/ui';
import api from '../../utils/api';

const EMPTY = { name: '', length_in: '', width_in: '', height_in: '', weight_lbs: '' };

function DimInput({ label, value, onChange }) {
  return (
    <div style={{ flex: 1 }}>
      <label className="form-label" style={{ fontSize: 11 }}>{label} (in)</label>
      <input
        type="number" step="0.01" min="0" className="form-control form-control-sm"
        value={value} onChange={e => onChange(e.target.value)}
        style={{ textAlign: 'right' }}
      />
    </div>
  );
}

function BoxForm({ initial = EMPTY, onSave, onCancel, saving }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const valid = form.name.trim() && form.length_in && form.width_in && form.height_in;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label className="form-label">Box Name</label>
        <input className="form-control" value={form.name}
          onChange={e => set('name', e.target.value)} placeholder="e.g. Shuttle Box" />
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <DimInput label="Length" value={form.length_in} onChange={v => set('length_in', v)} />
        <DimInput label="Width"  value={form.width_in}  onChange={v => set('width_in', v)} />
        <DimInput label="Height" value={form.height_in} onChange={v => set('height_in', v)} />
        <div style={{ flex: 1 }}>
          <label className="form-label" style={{ fontSize: 11 }}>Box Wt (lbs)</label>
          <input type="number" step="0.01" min="0" className="form-control form-control-sm"
            value={form.weight_lbs} onChange={e => set('weight_lbs', e.target.value)}
            style={{ textAlign: 'right' }} placeholder="0" />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn btn-sm btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn btn-sm btn-primary" onClick={() => onSave(form)} disabled={!valid || saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export default function BoxSizes() {
  const { data: rawBoxes, loading, error, refetch } = useFetch('/api/box-sizes');
  const boxes = rawBoxes || [];
  const [showAdd, setShowAdd]   = useState(false);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');
  const [showInactive, setShowInactive] = useState(false);

  const visible = showInactive ? boxes : boxes.filter(b => b.active);

  async function handleAdd(form) {
    setSaving(true); setErr('');
    try {
      await api.post('/api/box-sizes', form);
      setShowAdd(false);
      refetch();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  }

  async function handleEdit(form) {
    setSaving(true); setErr('');
    try {
      await api.put(`/api/box-sizes/${editId}`, form);
      setEditId(null);
      refetch();
    } catch (e) { setErr(e.response?.data?.error || e.message); }
    finally { setSaving(false); }
  }

  async function toggleActive(box) {
    try {
      await api.put(`/api/box-sizes/${box.id}`, { active: !box.active });
      refetch();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  }

  async function handleDelete(box) {
    if (!window.confirm(`Delete "${box.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/api/box-sizes/${box.id}`);
      refetch();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  }

  return (
    <div>
      <div className="page-header">
        <h1>Box Sizes</h1>
        <div className="page-actions">
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
            Show inactive
          </label>
          <button className="btn btn-sm btn-primary" onClick={() => { setShowAdd(true); setEditId(null); }}>
            <i className="ti ti-plus" /> Add Box Size
          </button>
        </div>
      </div>

      {err && <div className="alert alert-danger" style={{ marginBottom: 12 }}>{err}</div>}

      {showAdd && (
        <div className="card" style={{ marginBottom: 16, padding: 16 }}>
          <h3 style={{ marginBottom: 12, fontSize: 14, fontWeight: 600 }}>New Box Size</h3>
          <BoxForm onSave={handleAdd} onCancel={() => setShowAdd(false)} saving={saving} />
        </div>
      )}

      {loading ? <Spinner /> : error ? <ErrorBanner message={error} onRetry={refetch} /> : (
        <div className="card">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ textAlign: 'right' }}>L (in)</th>
                  <th style={{ textAlign: 'right' }}>W (in)</th>
                  <th style={{ textAlign: 'right' }}>H (in)</th>
                  <th style={{ textAlign: 'right' }}>Box Wt (lbs)</th>
                  <th style={{ textAlign: 'center' }}>Status</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {visible.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: 24, color: 'var(--text-tertiary)' }}>
                    No box sizes found
                  </td></tr>
                )}
                {visible.map(box => (
                  <React.Fragment key={box.id}>
                    <tr style={{ opacity: box.active ? 1 : 0.5 }}>
                      <td style={{ fontWeight: 500 }}>{box.name}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{parseFloat(box.length_in).toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{parseFloat(box.width_in).toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{parseFloat(box.height_in).toFixed(2)}</td>
                      <td style={{ textAlign: 'right', fontFamily: 'monospace', fontSize: 12 }}>{parseFloat(box.weight_lbs || 0).toFixed(2)}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          fontSize: 11, padding: '2px 8px', borderRadius: 12, fontWeight: 600,
                          background: box.active ? 'var(--green-bg, #dcfce7)' : 'var(--surface-2)',
                          color: box.active ? 'var(--green, #166534)' : 'var(--text-tertiary)',
                        }}>
                          {box.active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button className="btn btn-sm" style={{ marginRight: 4 }}
                          onClick={() => { setEditId(box.id); setShowAdd(false); }}>
                          <i className="ti ti-pencil" />
                        </button>
                        <button className="btn btn-sm" style={{ marginRight: 4 }}
                          title={box.active ? 'Deactivate' : 'Activate'}
                          onClick={() => toggleActive(box)}>
                          <i className={`ti ${box.active ? 'ti-eye-off' : 'ti-eye'}`} />
                        </button>
                        <button className="btn btn-sm" style={{ color: 'var(--red)' }}
                          onClick={() => handleDelete(box)}>
                          <i className="ti ti-trash" />
                        </button>
                      </td>
                    </tr>
                    {editId === box.id && (
                      <tr>
                        <td colSpan={7} style={{ background: 'var(--surface-2)', padding: '12px 16px' }}>
                          <BoxForm
                            initial={{
                              name: box.name,
                              length_in: box.length_in,
                              width_in: box.width_in,
                              height_in: box.height_in,
                              weight_lbs: box.weight_lbs || '',
                            }}
                            onSave={handleEdit}
                            onCancel={() => setEditId(null)}
                            saving={saving}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '8px 16px', fontSize: 12, color: 'var(--text-tertiary)', borderTop: '1px solid var(--border)' }}>
            {visible.length} box size{visible.length !== 1 ? 's' : ''}{!showInactive && boxes.some(b => !b.active) ? ` · ${boxes.filter(b => !b.active).length} inactive hidden` : ''}
          </div>
        </div>
      )}
    </div>
  );
}
